import React, { useState, useEffect, useCallback } from 'react';
import {
    format,
    parseISO,
    getISOWeek,
    getMonth,
    getYear,
    startOfWeek,
    endOfWeek,
    subDays,
    subYears,
    eachDayOfInterval,
    startOfMonth,
    endOfYear,
    subMonths as dateFnsSubMonths,
    subWeeks as dateFnsSubWeeks
} from 'date-fns';
import { es } from 'date-fns/locale';
import './ReporteClasico.css';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

const chartAreaBackgroundColorPlugin = {
  id: 'chartAreaBackgroundColor',
  beforeDraw(chart, args, pluginOptions) {
    const {ctx} = chart;
    const {color} = pluginOptions;
    if (color) {
      ctx.save();
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, chart.width, chart.height);
      ctx.restore();
    }
  }
};

ChartJS.register(
    CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, chartAreaBackgroundColorPlugin
);

function ReporteClasico() {
    const [activeTab, setActiveTab] = useState('Diaria');
    const initialYesterday = new Date();
    initialYesterday.setDate(initialYesterday.getDate() - 1);
    const [selectedDate, setSelectedDate] = useState(format(initialYesterday, 'yyyy-MM-dd'));
    
    const [selectedCompsetId, setSelectedCompsetId] = useState('');
    const [compsets, setCompsets] = useState([]);
    
    const [reportData, setReportData] = useState({ summary: [], accumulated: [], trend: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedChartMetric, setSelectedChartMetric] = useState('revenue');
    
    const [userLevel, setUserLevel] = useState('');

    useEffect(() => {
        const userString = localStorage.getItem('user');
        if (userString) {
            try {
                const user = JSON.parse(userString);
                setUserLevel(user.user_level);
            } catch (e) {
                console.error("Failed to parse user from localStorage", e);
            }
        }
    }, []);

    useEffect(() => {
        const today = new Date();
        let newDefaultDateString = '';
        switch (activeTab) {
            case 'Diaria':
                newDefaultDateString = format(subDays(today, 1), 'yyyy-MM-dd');
                break;
            case 'Semanal':
                newDefaultDateString = format(subDays(startOfWeek(today, { weekStartsOn: 1 }), 1), 'yyyy-MM-dd');
                break;
            case 'Mensual':
                newDefaultDateString = format(subDays(startOfMonth(today), 1), 'yyyy-MM-dd');
                break;
            case 'Anual':
                newDefaultDateString = format(endOfYear(subYears(today, 1)), 'yyyy-MM-dd');
                break;
            default:
                newDefaultDateString = format(subDays(today, 1), 'yyyy-MM-dd');
        }
        setSelectedDate(newDefaultDateString);
    }, [activeTab]);

    const getAuthHeader = useCallback(() => {
        const token = localStorage.getItem('token');
        return token ? { 'Authorization': `Bearer ${token}` } : {};
    }, []);
    
    useEffect(() => {
        const fetchInitialData = async () => {
            if (!userLevel) return;

            setLoading(true);
            setError(null);
            const userString = localStorage.getItem('user');
            const user = userString ? JSON.parse(userString) : null;
            
            // --- THIS IS THE FIX ---
            // Simplified logic for different user roles.
            if (userLevel === 'Ejecutivo (Restringido)') {
                const userDefaultCompSetId = user ? user.default_compset_id : null;
                if (userDefaultCompSetId) {
                    setSelectedCompsetId(userDefaultCompSetId);
                } else {
                    setError("No se encontró un compset por defecto para su usuario.");
                    setLoading(false);
                }
            } else {
                // Logic for all other users
                try {
                    const apiUrl = `${process.env.REACT_APP_API_URL}/api.php?path=compsets`;
                    const response = await fetch(apiUrl, { headers: getAuthHeader() });

                    if (!response.ok) throw new Error(`HTTP error fetching compsets! status: ${response.status}`);
                    
                    const data = await response.json();

                    if (data.success && Array.isArray(data.data) && data.data.length > 0) {
                        const fetchedCompsets = data.data;
                        setCompsets(fetchedCompsets);
                        
                        const userDefaultCompSetId = user ? user.default_compset_id : null;
                        const defaultExists = userDefaultCompSetId 
                            ? fetchedCompsets.some(c => c.comp_set_id == userDefaultCompSetId) 
                            : false;
                        
                        if (defaultExists) {
                            setSelectedCompsetId(userDefaultCompSetId);
                        } else {
                            setSelectedCompsetId(fetchedCompsets[0].comp_set_id);
                        }
                    } else {
                        throw new Error("No se encontraron compsets disponibles.");
                    }
                } catch (err) {
                    setError(err.message || "Failed to load compsets list.");
                    setLoading(false);
                }
            }
        };
        fetchInitialData();
    }, [getAuthHeader, userLevel]);


    const fetchReportData = useCallback(async () => {
        if (!selectedCompsetId) {
             // This check prevents fetching when the compset ID isn't set yet.
             // The loading state is now primarily handled by the initial data fetch.
             return;
        }

        setLoading(true);
        setError(null);
        try {
            let queryParams = new URLSearchParams({
                type: activeTab.toLowerCase(),
                compsetId: selectedCompsetId,
            });

            const currentParsedDate = parseISO(selectedDate);
            switch (activeTab) {
                case 'Diaria':
                    queryParams.append('date', selectedDate);
                    break;
                case 'Semanal':
                    const startWeek = startOfWeek(currentParsedDate, { weekStartsOn: 1 });
                    const endWeek = endOfWeek(currentParsedDate, { weekStartsOn: 1 });
                    queryParams.append('startDate', format(startWeek, 'yyyy-MM-dd'));
                    queryParams.append('endDate', format(endWeek, 'yyyy-MM-dd'));
                    break;
                case 'Mensual':
                    queryParams.append('month', (getMonth(currentParsedDate) + 1).toString());
                    queryParams.append('year', getYear(currentParsedDate).toString());
                    break;
                case 'Anual':
                    queryParams.append('year', getYear(currentParsedDate).toString());
                    break;
                default:
                    setLoading(false);
                    return;
            }

            const apiUrl = `${process.env.REACT_APP_API_URL}/api.php?path=reports/clasico&${queryParams.toString()}`;
            const response = await fetch(apiUrl, {
                headers: getAuthHeader()
            });

            if (!response.ok) {
               const errorText = await response.text();
               try {
                   const errorJson = JSON.parse(errorText);
                   throw new Error(errorJson.error?.message || `API Error: ${response.status}`);
               } catch (e) {
                   throw new Error(`HTTP error fetching report data! status: ${response.status}. Response: ${errorText}`);
               }
            }
            const fullReportData = await response.json();

            if (fullReportData.success) {
                setReportData({
                    summary: fullReportData.data.summaryInfo || [],
                    accumulated: fullReportData.data.accumulatedInfo || [],
                    trend: fullReportData.data.trend || [],
                });
            } else {
                throw new Error(fullReportData.error?.message || "API reported success:false");
            }
        } catch (err) {
            setError(err.message || `Failed to load ${activeTab} report data.`);
            setReportData({ summary: [], accumulated: [], trend: [] });
        } finally {
            setLoading(false);
        }
    }, [activeTab, selectedDate, selectedCompsetId, getAuthHeader]);
    
    useEffect(() => {
        if (selectedCompsetId) {
            fetchReportData();
        }
    }, [fetchReportData, selectedCompsetId]);

    const getHotelColor = useCallback((hotelId) => {
        const colors = ['#42A5F5', '#66BB6A', '#FFA726', '#EF5350', '#AB47BC', '#FFCA28', '#26A69A', '#78909C', '#F44336', '#E91E63', '#9C27B0', '#3F51B5', '#03A9F4', '#00BCD4', '#4CAF50', '#CDDC39'];
        const numericHotelId = parseInt(hotelId);
        if (isNaN(numericHotelId)) return colors[0];
        const index = (numericHotelId - 1) % colors.length;
        return colors[index < 0 ? 0 : index];
    }, []);

    const prepareChartData = useCallback((trendItems, hotelsList, metric, currentTab, dateRefString) => {
       if (!trendItems || trendItems.length === 0 || !hotelsList || hotelsList.length === 0) {
           return { labels: [], datasets: [] };
       }
        let uniqueLabels = [];
        const dateRef = parseISO(dateRefString);
        let labelGenerationArray = [];
        switch (currentTab) {
            case 'Diaria':
                const endDateForTrend = dateRef;
                const startDateForTrend = subDays(endDateForTrend, 9);
                labelGenerationArray = eachDayOfInterval({ start: startDateForTrend, end: endDateForTrend });
                uniqueLabels = labelGenerationArray.map(date => format(date, 'MMM dd', { locale: es }));
                break;
            case 'Semanal':
                 const selectedWeekEndDate = endOfWeek(dateRef, { weekStartsOn: 1 });
                 for (let i = 11; i >= 0; i--) {
                       labelGenerationArray.push(startOfWeek(dateFnsSubWeeks(selectedWeekEndDate, i), { weekStartsOn: 1 }));
                 }
                 uniqueLabels = labelGenerationArray.map(date => `W${getISOWeek(date)} '${getYear(date).toString().substring(2)}`);
                 break;
            case 'Mensual':
                 const endDateForMonthlyTrend = startOfMonth(dateRef);
                 for (let i = 11; i >= 0; i--) {
                       labelGenerationArray.push(dateFnsSubMonths(endDateForMonthlyTrend, i));
                 }
                 uniqueLabels = labelGenerationArray.map(date => format(date, 'MMM yy', { locale: es }));
                 break;
            case 'Anual':
                 const selectedReportYear = getYear(dateRef);
                 for (let i = 9; i >= 0; i--) {
                       labelGenerationArray.push((selectedReportYear - i).toString());
                 }
                 uniqueLabels = labelGenerationArray;
                 break;
            default:
                uniqueLabels = [];
        }
        const datasets = hotelsList.map(hotel => {
            const hotelData = trendItems.filter(d => d.hotel_id.toString() === hotel.hotel_id.toString());
            const dataPoints = uniqueLabels.map((label, index) => {
                let itemData;
                if (currentTab === 'Diaria') {
                    const targetDate = labelGenerationArray[index];
                    itemData = hotelData.find(d => d.date && format(parseISO(d.date), 'yyyy-MM-dd') === format(targetDate, 'yyyy-MM-dd'));
                } else if (currentTab === 'Semanal') {
                    const targetWeekDate = labelGenerationArray[index];
                    const targetYear = getYear(targetWeekDate);
                    const targetISOWeek = getISOWeek(targetWeekDate);
                    itemData = hotelData.find(d => d.year === targetYear && d.week === targetISOWeek);
                } else if (currentTab === 'Mensual') {
                    const targetMonthDate = labelGenerationArray[index];
                    const targetYear = getYear(targetMonthDate);
                    const targetMonth = getMonth(targetMonthDate) + 1;
                    itemData = hotelData.find(d => d.year === targetYear && d.month === targetMonth);
                } else if (currentTab === 'Anual') {
                    itemData = hotelData.find(d => d.year && d.year.toString() === label);
                }
                const value = itemData && itemData[metric] !== undefined && !isNaN(parseFloat(itemData[metric])) ? parseFloat(itemData[metric]) : 0;
                return value;
            });
            return {
                label: hotel.name,
                data: dataPoints,
                borderColor: getHotelColor(hotel.hotel_id),
                backgroundColor: getHotelColor(hotel.hotel_id) + '40',
                tension: 0.3,
                pointRadius: 3,
                pointHoverRadius: 5,
                fill: false,
                metricKey: metric
            };
        });
        return {
            labels: uniqueLabels,
            datasets: datasets.filter(ds => ds.data.some(dp => dp !== 0)),
        };
    }, [getHotelColor]);

    const renderNumber = (value, decimals = 2) => {
        const num = parseFloat(value);
        if (isNaN(num)) { return 'N/A'; }
        return num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    };

    const getMetricDetails = useCallback((metric, currentTab) => {
        let titlePrefix = '';
        switch (currentTab) { case 'Diaria': titlePrefix = 'Diaria'; break; case 'Semanal': titlePrefix = 'Semanal'; break; case 'Mensual': titlePrefix = 'Mensual'; break; case 'Anual': titlePrefix = 'Anual'; break; default: titlePrefix = ''; }
        switch (metric) {
            case 'rooms_sold': return { title: `Vendidas ${titlePrefix}`, formatForChart: (val) => renderNumber(val, 0) };
            case 'occupancy_percent': return { title: `% Ocupación ${titlePrefix}`, formatForChart: (val) => renderNumber(val) + '%' };
            case 'revenue': return { title: `Ingreso ${titlePrefix}`, formatForChart: (val) => '$' + renderNumber(val, 2) };
            case 'average_rate': return { title: `Tarifa Promedio ${titlePrefix}`, formatForChart: (val) => '$' + renderNumber(val, 2) };
            case 'tarifa_efectiva': return { title: `Tarifa Efectiva ${titlePrefix}`, formatForChart: (val) => '$' + renderNumber(val, 2) };
            default: return { title: '', formatForChart: (val) => val };
        }
    }, []);

    const getTableHeaders = useCallback((tab) => {
       switch (tab) {
            case 'Diaria': return { summary: ["Hotel", "Vendidas", "% Ocupación", "Ingreso", "Tarifa Promedio", "Tarifa Efectiva"], accumulated: ["Hotel", "Vendidas Acum. Mes", "% Ocup. Acum. Mes", "Ingreso Acum. Mes", "Tarifa Prom. Acum. Mes", "Tarifa Efect. Acum. Mes"] };
            case 'Semanal': return { summary: ["Hotel", "Total Vend. Sem.", "% Ocup. Sem.", "Ingreso Sem.", "Tarifa Prom. Sem.", "Tarifa Efect. Sem."], accumulated: ["Hotel", "Vendidas Acumuladas YTD", "% Ocup. Acumulada YTD", "Ingreso Acumulado YTD", "Tarifa Prom. Acumulada YTD", "Tarifa Efect. Acumulada YTD"] };
            case 'Mensual': return { summary: ["Hotel", "Total Vend. Men.", "% Ocup. Men.", "Ingreso Men.", "Tarifa Prom. Men.", "Tarifa Efect. Men."], accumulated: ["Hotel", "Vendidas Acumuladas YTD", "% Ocup. Acumulada YTD", "Ingreso Acumulado YTD", "Tarifa Prom. Acumulada YTD", "Tarifa Efect. Acumulada YTD"] };
            case 'Anual': return { summary: ["Hotel", "Total Vend. Anual", "% Ocup. Anual", "Ingreso Anual", "Tarifa Prom. Anual.", "Tarifa Efect. Anual"], accumulated: ["Hotel", "Vendidas Acumuladas", "% Ocup. Acumulada", "Ingreso Acumulado", "Tarifa Prom. Acumulada", "Tarifa Efect. Acumulada"] };
            default: return { summary: [], accumulated: [] };
        }
    }, []);

    const getTableRows = useCallback((data, tab, type = 'summary') => {
        if (!data || data.length === 0) {
            return null;
        }
        return data.map((row, index) => {
            const key = `${type}-${row.hotel_id || index}-${index}`;
            if (tab === 'Diaria' && type === 'summary') {
                return (<tr key={key}><td>{row.hotel_name}</td><td>{renderNumber(row.rooms_sold, 0)}</td><td>{renderNumber(row.occupancy_percent)}%</td><td>${renderNumber(row.revenue, 2)}</td><td>${renderNumber(row.average_rate, 2)}</td><td>${renderNumber(row.tarifa_efectiva, 2)}</td></tr>);
            }
            if (tab === 'Diaria' && type === 'accumulated') {
                return (<tr key={key}><td>{row.hotel_name}</td><td>{renderNumber(row.accumulated_rooms_sold, 0)}</td><td>{renderNumber(row.accumulated_occupancy_percent)}%</td><td>${renderNumber(row.accumulated_revenue, 2)}</td><td>${renderNumber(row.accumulated_average_rate, 2)}</td><td>${renderNumber(row.accumulated_tarifa_efectiva, 2)}</td></tr>);
            }
            if (tab === 'Semanal' && type === 'summary') {
                return (<tr key={key}><td>{row.hotel_name}</td><td>{renderNumber(row.total_rooms_sold || 0, 0)}</td><td>{renderNumber(row.total_occupancy_percent || 0)}%</td><td>${renderNumber(row.total_revenue || 0, 2)}</td><td>${renderNumber(row.total_average_rate || 0, 2)}</td><td>${renderNumber(row.total_tarifa_efectiva || 0, 2)}</td></tr>);
            }
            if (tab === 'Semanal' && type === 'accumulated') {
                return (<tr key={key}><td>{row.hotel_name}</td><td>{renderNumber(row.ytd_rooms_sold || 0, 0)}</td><td>{renderNumber(row.ytd_occupancy_percent || 0)}%</td><td>${renderNumber(row.ytd_revenue || 0, 2)}</td><td>${renderNumber(row.ytd_average_rate || 0, 2)}</td><td>${renderNumber(row.ytd_tarifa_efectiva || 0, 2)}</td></tr>);
            }
            if ((tab === 'Mensual' || tab === 'Anual') && type === 'summary') {
                return (<tr key={key}><td>{row.hotel_name}</td><td>{renderNumber(row.total_rooms_sold || 0, 0)}</td><td>{renderNumber(row.total_occupancy_percent || 0)}%</td><td>${renderNumber(row.total_revenue || 0, 2)}</td><td>${renderNumber(row.total_average_rate || 0, 2)}</td><td>${renderNumber(row.total_tarifa_efectiva || 0, 2)}</td></tr>);
            }
            if ((tab === 'Mensual' || tab === 'Anual') && type === 'accumulated') {
                 return (<tr key={key}><td>{row.hotel_name}</td><td>{renderNumber(row.ytd_rooms_sold || 0, 0)}</td><td>{renderNumber(row.ytd_occupancy_percent || 0)}%</td><td>${renderNumber(row.ytd_revenue || 0, 2)}</td><td>${renderNumber(row.ytd_average_rate || 0, 2)}</td><td>${renderNumber(row.ytd_tarifa_efectiva || 0, 2)}</td></tr>);
            }
            return null;
        });
    }, []);
    
    const isRestrictedUser = userLevel === 'Ejecutivo (Restringido)';

    const uniqueHotelsInTrend = !reportData.trend ? [] : 
        Array.from(new Map(reportData.trend.map(item => [item.hotel_id, {hotel_id: item.hotel_id, name: item.hotel_name}])).values());
    const chartData = prepareChartData(reportData.trend, uniqueHotelsInTrend, selectedChartMetric, activeTab, selectedDate);
    const metricDetails = getMetricDetails(selectedChartMetric, activeTab);
    const { summary: summaryHeaders, accumulated: accumulatedHeaders } = getTableHeaders(activeTab);
    const chartOptions = { responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false }, plugins: { legend: { position: 'top', labels: { boxWidth: 12, padding: 15, font: { size: 10 } } }, title: { display: true, text: metricDetails.title || `Tendencia ${activeTab}`, font: { size: 14, weight: 'bold' }, padding: { top: 5, bottom: 10 } }, tooltip: { backgroundColor: 'rgba(0,0,0,0.7)', titleFont: { size: 12, weight: 'bold' }, bodyFont: { size: 11 }, footerFont: { size: 10 }, padding: 10, cornerRadius: 4, displayColors: true, boxPadding: 3, callbacks: { label: function(context) { let label = context.dataset.label || ''; if (label) { label += ': '; } if (context.parsed.y !== null) { const details = getMetricDetails(context.dataset.metricKey || selectedChartMetric, activeTab); label += details.formatForChart ? details.formatForChart(context.parsed.y) : context.parsed.y; } return label; } } }, chartAreaBackgroundColor: { color: 'rgba(250, 250, 250, 0.9)' } }, scales: { x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 45, minRotation: 0 } }, y: { grid: { color: 'rgba(200, 200, 200, 0.2)', borderDash: [2, 2], }, ticks: { font: { size: 10 }, callback: function(value) { const details = getMetricDetails(selectedChartMetric, activeTab); if (details.formatForChart) { if (selectedChartMetric === 'occupancy_percent') return details.formatForChart(value).replace('%','') + '%'; return details.formatForChart(value).replace('$', ''); } return value; } } } }, elements: { line: { borderWidth: 1.5 }, point: { radius: 1.5, hoverRadius: 3 } } };
    
    const getSummaryTitle = () => { try { const refDate = parseISO(selectedDate); switch (activeTab) { case 'Diaria': return `Métrica del día (${format(refDate, 'dd/MM/yyyy', { locale: es })})`; case 'Semanal': const weekStart = startOfWeek(refDate, { weekStartsOn: 1 }); const weekEnd = endOfWeek(refDate, { weekStartsOn: 1 }); const weekNum = getISOWeek(weekStart); return `Métrica de la semana #${weekNum} (${format(weekStart, 'dd/MM/yyyy')}) a (${format(weekEnd, 'dd/MM/yyyy')})`; case 'Mensual': return `Métrica del mes de ${format(refDate, 'MMMM, yyyy', { locale: es })}`; case 'Anual': return `Métrica del año ${getYear(refDate)}`; default: return "Métrica"; } } catch (e) { return "Métrica"; } };
    const generateWeekOptions = () => { const today = new Date(); const endOfLastWeek = subDays(startOfWeek(today, { weekStartsOn: 1 }), 1); let options = []; for (let i = 0; i < 52; i++) { const week = dateFnsSubWeeks(endOfLastWeek, i); const weekStart = startOfWeek(week, { weekStartsOn: 1 }); const weekEnd = endOfWeek(week, { weekStartsOn: 1 }); const weekNum = getISOWeek(week); const label = `Semana ${weekNum} (${format(weekStart, 'dd MMM', { locale: es })} - ${format(weekEnd, 'dd MMM yy', { locale: es })})`; options.push(<option key={i} value={format(week, 'yyyy-MM-dd')}>{label}</option>); } return options; };
    const generateMonthOptions = () => { const today = new Date(); const lastMonth = startOfMonth(subDays(startOfMonth(today), 1)); let options = []; for (let i = 0; i < 12; i++) { const month = dateFnsSubMonths(lastMonth, i); const label = format(month, 'MMMM, yyyy', { locale: es }); options.push(<option key={i} value={format(month, 'yyyy-MM-dd')}>{label}</option>); } return options; };
    const generateYearOptions = () => { const currentYear = getYear(new Date()); let options = []; for (let i = 0; i < 10; i++) { const year = currentYear - i; options.push(<option key={year} value={`${year}-12-31`}>{year}</option>); } return options; };
    
return (
        <div className="reporte-clasico-container">
            <header className="main-header"> 
                <h1 className="page-title">Reporte Clásico</h1> 
            </header>
            
            <section className="info-entry-section report-tabs-section">
                <div className="report-tabs">
                    {['Diaria', 'Semanal', 'Mensual', 'Anual'].map(tabName => (
                        <button 
                            key={tabName} 
                            className={`report-tab-button ${activeTab === tabName ? 'active' : ''}`} 
                            onClick={() => setActiveTab(tabName)}
                        >
                            {tabName}
                        </button>
                    ))}
                </div>
                <div className="report-controls">
                    <div className="control-group">
                        <label htmlFor="date-input">
                            {activeTab === 'Diaria' ? 'Fecha:' : activeTab === 'Semanal' ? 'Semana:' : activeTab === 'Mensual' ? 'Mes:' : 'Año:'}
                        </label>
                        {activeTab === 'Diaria' && <input type="date" id="date-input" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />}
                        {activeTab === 'Semanal' && <select id="date-input" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}>{generateWeekOptions()}</select>}
                        {activeTab === 'Mensual' && <select id="date-input" value={format(parseISO(selectedDate), 'yyyy-MM-dd')} onChange={(e) => setSelectedDate(e.target.value)}>{generateMonthOptions()}</select>}
                        {activeTab === 'Anual' && <select id="date-input" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}>{generateYearOptions()}</select>}
                    </div>
                    {!isRestrictedUser && (
                        <div className="control-group">
                            <label htmlFor="compset-selector">Compset:</label>
                            <select 
                                id="compset-selector" 
                                value={selectedCompsetId} 
                                onChange={(e) => setSelectedCompsetId(e.target.value)} 
                                disabled={compsets.length === 0}
                            >
                                {compsets.map(compset => (
                                    <option key={compset.comp_set_id} value={compset.comp_set_id}>
                                        {compset.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            </section>

            {/* STATUS MESSAGES: Rendered inside the main container to keep the layout stable */}
            {loading && (
                <section className="info-entry-section">
                    <p>Cargando reporte...</p>
                </section>
            )}

            {error && (
                <section className="info-entry-section" style={{ color: 'red', textAlign: 'center' }}>
                    <p>Error: {error}</p>
                    {/* Emergency Logout for Mobile Users */}
                    <button 
                        onClick={() => { localStorage.clear(); window.location.href = '/login'; }}
                        className="report-tab-button active"
                        style={{ marginTop: '10px', padding: '10px 20px', borderRadius: '4px' }}
                    >
                        Cerrar Sesión y Reintentar
                    </button>
                </section>
            )}

            {/* DATA CONTENT: Only renders when ready and no errors exist */}
            {!loading && !error && (reportData.summary.length > 0 || reportData.accumulated.length > 0 || reportData.trend.length > 0) ? (
                <>
                    {reportData.summary.length > 0 && (
                        <section className="info-entry-section report-table-section">
                            <h2 className="report-subtitle">{getSummaryTitle()}</h2>
                            <div className="data-table-container">
                                <table className="data-input-table">
                                    <thead>
                                        <tr>{summaryHeaders.map((h, i) => <th key={`sum-th-${i}`}>{h}</th>)}</tr>
                                    </thead>
                                    <tbody>{getTableRows(reportData.summary, activeTab, 'summary')}</tbody>
                                </table>
                            </div>
                        </section>
                    )}

                     {reportData.accumulated.length > 0 && activeTab !== 'Anual' && (
                        <section className="info-entry-section report-table-section">
                            <h2 className="report-subtitle">Información Acumulada</h2>
                            <div className="data-table-container">
                                <table className="data-input-table">
                                      <thead><tr>{accumulatedHeaders.map((h, i) => <th key={`acc-th-${i}`}>{h}</th>)}</tr></thead>
                                      <tbody>{getTableRows(reportData.accumulated, activeTab, 'accumulated')}</tbody>
                                   </table>
                            </div>
                        </section>
                    )}

                    {reportData.trend.length > 0 && chartData.datasets.length > 0 ? (
                        <section className="info-entry-section report-chart-section">
                            <h2 className="report-subtitle">Gráfico de Tendencias</h2>
                            <div className="chart-metric-selector">
                                <div className="control-group">
                                    <label htmlFor="chart-metric">Mostrar Tendencia de:</label>
                                    <select id="chart-metric" value={selectedChartMetric} onChange={(e) => setSelectedChartMetric(e.target.value)}>
                                        <option value="revenue">Ingreso</option>
                                        <option value="rooms_sold">Vendidas</option>
                                        <option value="occupancy_percent">% Ocupación</option>
                                        <option value="average_rate">Tarifa Promedio</option>
                                        <option value="tarifa_efectiva">Tarifa Efectiva</option>
                                    </select>
                                </div>
                            </div>
                            <div className="chart-container" style={{ height: '400px', position: 'relative' }}>
                                <Line data={chartData} options={chartOptions} />
                            </div>
                        </section>
                    ) : ( !loading && <section className="info-entry-section"><p>No hay datos de tendencia para graficar para la selección actual.</p></section> )}
                </>
            ) : (
                !loading && !error && <section className="info-entry-section"><p>No hay datos disponibles para la selección actual.</p></section>
            )}
        </div>
    );
}

export default ReporteClasico;
